import { Component, HostListener, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { Entity } from './data/schema/entity';
import { ServiceHelper } from './helper/services.helper';
import { AuthenticationService } from './modules/auth-core/data/service/authentication.service';
import { Log } from './log';

@Component({ template: '' })
export abstract class BasePage implements OnDestroy  {

    isMobile = true;
    hasChanges = false;

    pageState = new PageState();

    // to avoid logout code all over the place, and we'll use this label as
    // a sort of confirmation step
    logoutLabel = 'Logout';
    
    defaultBack: string = '/home';

    // monitors if user logs out, and if so, takes user to login
    private loginStateSub?: Subscription;
    private enableChangeChecking = false;

    // this is for monitoring if page contents have changed so we can enable/disable
    // save button, and also prompt to save before leaving page
    private changeCheckInterval: any; // the interval that compares orig to curr object, needs to be destroyed on leave
    private origObj?: string; // the initially loaded object to compare current object to, and to reset to
    private currObj: any; // the rslt object that is being rendered/edited on screen

    constructor(
        protected authService?: AuthenticationService
    ) {
        this.isMobile = (window.innerWidth < 800);

        this.loginStateSub = ServiceHelper.authSvc.loginStateChanged.subscribe(state => {
            if (state === 0 && ServiceHelper.navCtrl != null) {
                Log.debug('logout detected, take user to login page');
                ServiceHelper.navCtrl.navigateRoot('/auth/login');
            }
        });
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.isMobile = (window.innerWidth < 800);
    }

    ngOnDestroy() {
        if (this.loginStateSub != null) {
            this.loginStateSub.unsubscribe();
        }
    }

    // canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    //     return new Promise((resolve) => {
    //         Log.debug('BasePage.canDeactivate called, hasChanges: ' + this.hasChanges);
    //         if (this.enableChangeChecking === true && this.wasObjModified() === true) {
    //             this.promptForSave(this.currObj).then((response) => {
    //                 Log.debug('prompt for save dismissed with ', response);
    //                 resolve(response as boolean);
    //             });
    //         } else {
    //             resolve(true);
    //         }
    //     });
    // }

    ionViewDidLeave() {
        clearInterval(this.changeCheckInterval);
    }

    async baseBack(){
        // super hacky, lets see if window location changes to know if pop worked...
        const beforeLocation = window.location.href;
        await ServiceHelper.navCtrl.pop()
        
        if ( beforeLocation == window.location.href) {
            // we went nowhere, use default
            ServiceHelper.navCtrl.navigateBack(this.defaultBack);
        }
    }

    dateCheck(start: Date, end: Date) {
        return new Promise<boolean>((resolve) => {
            const now = new Date().valueOf();
            if (start.valueOf() >= end.valueOf()) {
                ServiceHelper.displayAlert('Invalid Dates', 'The Raffle start date/time must come before the end date/time.');
                return resolve(false);
            } else if (now >= end.valueOf()) {
                ServiceHelper.displayAlert('Invalid End', 'The Raffle end date/time must be in the future.');
                return resolve(false);
            }
            resolve(true);
        });
    }

    /**
     * Function to get time from a date string
     *
     * @param date string to retrieve time from for a time input
     * @returns formatted time for a time input
     */
    getTime(date: string, includeMins: boolean = true) {
        let hours = `${new Date(date).getHours()}`;
        let minutes = `${new Date(date).getMinutes()}`;
        hours = hours.length === 1 ? `0${hours}` : hours;
        // if we don't want mins, just return the time
        if (includeMins === false) {
            return `${hours}:00`;
        }
        minutes = minutes.length === 1 ? `0${minutes}` : minutes;
        return `${hours}:${minutes}`;
    }
    /**
     * one logout function for all areas of app, can either flip a label to ask for confirmation
     * or use a confirmation dialog
     *
     * @param useAlertToConfirm
     */
    async logout(useAlertToConfirm: boolean = false,) {
        Log.debug('logout clicked');
        if (useAlertToConfirm) {
            if (await this.confirmSomething('Confirm Logout', 'Are you sure you want to logout?') === true) {
                ServiceHelper.authSvc.logout();
            }
        } else {
            if (this.logoutLabel === 'Logout') {
                this.logoutLabel = 'Confirm Logout?';
                setTimeout(() => { this.logoutLabel = 'Logout'; }, 3000);
            }
            else { ServiceHelper.authSvc.logout(); }
        }
    }

    async promptForSave(entity: Entity) {
        return new Promise(async (resolve) => {
            const confirm = await ServiceHelper.alertCtrl.create({
                header: 'Save Changes?',
                message: 'Would you like to save your changes?',
                buttons: [
                    {
                        text: 'Cancel',
                        handler: () => {
                            resolve(false);
                        }
                    }, {
                        text: 'No',
                        handler: () => {
                            confirm.dismiss().then(() => {
                                this.restoreObjState();
                                resolve(true);
                            });
                        }
                    }, {
                        text: 'Yes',
                        handler: () => {
                            confirm.dismiss().then(async () => {
                                await entity.save(this.pageState);
                                resolve(true);
                            });
                        }
                    }
                ]
            });
            confirm.present();
        });
    }

    async inputSomething(title: string, body: string, cancelLbl: string = 'Cancel', confirmLbl: string = 'Confirm', placeholderTxt?: string) {
        return new Promise(async (resolve) => {
            // create modal alert
            const inputAlert = await ServiceHelper.alertCtrl.create({
                header: title,
                message: body,
                inputs: [
                    {
                        name: 'input',
                        placeholder: placeholderTxt,

                    },
                ],
                buttons: [
                    {
                        text: cancelLbl,
                        role: 'cancel',
                        handler: (data: any) => resolve(null)
                    },
                    {
                        text: confirmLbl,
                        handler: (data: any) => resolve(data.input)
                    }
                ]
            });
            inputAlert.present();
        });
    }

    async confirmSomething(title: string, body: string, cancelLbl: string = 'Cancel', confirmLbl: string = 'Confirm'): Promise<boolean> {
        return new Promise(async (resolve) => {
            // create modal alert
            const confirmationAlert = await ServiceHelper.alertCtrl.create({
                header: title,
                message: body,
                buttons: [
                    {
                        text: cancelLbl,
                        role: 'cancel',
                        handler: () => resolve(false)
                    },
                    {
                        text: confirmLbl,
                        handler: () => resolve(true)
                    }
                ]
            });
            confirmationAlert.present();
        });
    }

    async promptForSaveSomething(title: string, body: string) {
        return new Promise(async (resolve) => {
            const confirm = await ServiceHelper.alertCtrl.create({
                header: title,
                message: body,
                buttons: [
                    {
                        text: 'Cancel',
                        handler: () => {
                            resolve('cancel');
                        }
                    }, {
                        text: 'No',
                        handler: () => {
                            confirm.dismiss().then(() => {
                                resolve('no');
                            });
                        }
                    }, {
                        text: 'Yes',
                        handler: () => {
                            confirm.dismiss().then(async () => {
                                resolve('yes');
                            });
                        }
                    }
                ]
            });
            confirm.present();
        });
    }

    async askForValues(title: string, body: string, labels: string[], inputType = 'text', cancelLbl: string = 'Cancel', confirmLbl: string = 'Confirm',): Promise<string[]> {
        const inputs: any[] = [];
        labels.forEach(lbl => {
            inputs.push(
                {
                    type: inputType,
                    label: lbl,
                    value: ''
                });
        });
        return new Promise(async (resolve) => {
            // create modal alert
            const infoRequestAlert = await ServiceHelper.alertCtrl.create({
                header: title,
                message: body,
                inputs,
                buttons: [
                    {
                        text: cancelLbl,
                        role: 'cancel',
                        handler: () => resolve([])
                    },
                    {
                        text: confirmLbl,
                        handler: data => {
                            Log.debug(data);
                            resolve(Object.values(data));
                        }
                    }
                ]
            });
            infoRequestAlert.present();
        });
    }




    /**
     * Can be overwridden in case special way of checking for changes
     *
     * @returns whether object changed or not
     */
    protected wasObjModified(): boolean {
        return (JSON.stringify(this.currObj) !== this.origObj);
    }


    private saveObjState() {
        this.origObj = JSON.stringify(this.currObj);
    }

    private restoreObjState() {
        Object.assign(this.currObj, JSON.parse(this.origObj ?? '{}'));
    }

    private startChangeChecking(startObj: any) {
        if (this.enableChangeChecking === true) {
            this.currObj = startObj;
            this.origObj = JSON.stringify(startObj);
            this.changeCheckInterval = setInterval(m => {
                this.hasChanges = this.wasObjModified();
                //Log.debug('has changes? ' + this.hasChanges)
            }, 1000);
        }
    }
}

export class PageState {
    public busy: boolean = false;
    public loaded: boolean = false;
}

